import { A4_HEIGHT_MM, A4_WIDTH_MM } from '@/lib/constants/pdf.ts';
import { GeneratePdfParams } from '@/lib/types/power-of-attorney.ts';

export const generatePdf = async ({ targetElement }: GeneratePdfParams): Promise<Blob> => {
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import('html2canvas'), import('jspdf')]);

  await new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
  });

  const canvas = await html2canvas(targetElement, {
    backgroundColor: '#ffffff',
    scale: Math.min(window.devicePixelRatio || 1, 2),
    useCORS: true
  });

  const imageData = canvas.toDataURL('image/png');
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: false
  });

  pdf.addImage(imageData, 'PNG', 0, 0, A4_WIDTH_MM, A4_HEIGHT_MM);

  return pdf.output('blob');
};

export const dataUrlToBlob = async (dataUrl: string): Promise<Blob> => {
  const response = await fetch(dataUrl);

  if (!response.ok) {
    throw new Error('이미지를 변환하지 못했습니다.');
  }

  return response.blob();
};

export const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error('파일을 Base64로 변환하지 못했습니다.'));
        return;
      }

      const [, base64 = ''] = reader.result.split(',');

      if (!base64) {
        reject(new Error('파일을 Base64로 변환하지 못했습니다.'));
        return;
      }

      resolve(base64);
    };

    reader.onerror = () => {
      reject(reader.error ?? new Error('파일을 Base64로 변환하지 못했습니다.'));
    };

    reader.readAsDataURL(blob);
  });
};

export const loadDocumentHtmlTemplate = async (templatePath: string): Promise<string> => {
  const response = await fetch(templatePath);

  if (!response.ok) {
    throw new Error('템플릿을 불러오지 못했습니다.');
  }

  return response.text();
};

export const buildDocumentHtml = (template: string, formValues: Record<string, string>): string => {
  return Object.entries(formValues).reduce((html, [key, value]) => {
    return html.replaceAll(`{{${key}}}`, value);
  }, template);
};
